import React, { useState, useEffect } from 'react';
import './ProgramSwitcher.css';
import collegeConfig from '../../config/collegeConfig';
import TeacherDashboard from './TeacherDashboard';
import UGTeacherReview from '../UGTeacherReview/UGTeacherReview';

// Renders the right dashboard(s) for a logged-in teacher, and — only when
// relevant — a small floating PG/UG toggle. A teacher only has UG subjects
// if an admin explicitly assigned them via Manage Teachers (see
// server/models/UGSubject.js's teachers[] array), so this fetches that
// assignment fresh on every mount rather than trusting a stale login
// snapshot (matches how TeacherDashboard.js already re-fetches
// GET /teachers/:userId on every mount for PG course assignment).
const ProgramSwitcher = ({ user }) => {
  const [assignedUGSubjects, setAssignedUGSubjects] = useState(null); // null = still loading
  const [activeTab, setActiveTab] = useState(null); // decided once subjects load

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userId = user?.id || user?._id;
    if (!userId) return;

    fetch(`${collegeConfig.api.baseURL}/teachers/${userId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        const subjects = data.teacher?.assignedUGSubjects || [];
        setAssignedUGSubjects(subjects);
        // Default to UG only when the teacher has UG subjects but no PG
        // courses at all — otherwise PG is the default landing view.
        const hasPGCourses = (user?.assignedCourses || []).length > 0;
        setActiveTab(subjects.length > 0 && !hasPGCourses ? 'ug' : 'pg');
      })
      .catch(err => {
        console.error('Error fetching UG subject assignment:', err);
        setAssignedUGSubjects([]);
        setActiveTab('pg');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Still loading, or confirmed no UG subjects assigned — PG only, no switcher.
  if (!assignedUGSubjects || assignedUGSubjects.length === 0) {
    return <TeacherDashboard />;
  }

  // Rendered *inside* each dashboard's own header (next to the notification
  // bell / profile avatar) rather than as a floating overlay — a fixed
  // pill was sitting on top of the profile avatar and didn't adapt well on
  // mobile. Passing it down as a prop lets each dashboard place it in its
  // already-responsive header row instead.
  const switcherButtons = (
    <div className="program-switcher">
      <button
        className={activeTab === 'pg' ? 'active' : ''}
        onClick={() => setActiveTab('pg')}
        title="PG Dashboard"
      >
        PG
      </button>
      <button
        className={activeTab === 'ug' ? 'active' : ''}
        onClick={() => setActiveTab('ug')}
        title="UG Review"
      >
        UG
      </button>
    </div>
  );

  return activeTab === 'pg'
    ? <TeacherDashboard programSwitcher={switcherButtons} />
    : <UGTeacherReview programSwitcher={switcherButtons} />;
};

export default ProgramSwitcher;
